import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Layers, Clock, ArrowRight, Sparkles, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatRelativeTime(iso) {
  if (!iso) return "recently";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "recently";
  const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

function deckHref(deck) {
  return `/flashspecs?deck=${encodeURIComponent(deck.deck_name)}${
    deck.last_card_id ? `&card=${encodeURIComponent(deck.last_card_id)}` : ""
  }`;
}

/**
 * "Continue Studying" quick-access card. Shows recently-touched decks with
 * progress + resume, or — when the user has no history yet — falls back to
 * suggested starter decks so the section is never empty. Mounted on both the
 * admin `Dashboard.jsx` and the staff `UserDashboard.jsx`.
 *
 * `variant="hero"` (Phase 6, UserDashboard only): renders the single most
 * recent deck as one prominent resume action with the actual last-viewed
 * cocktail name + studied count (both real persisted fields — nothing
 * inferred), and any remaining recent decks as a smaller "Recently Studied"
 * strip underneath. `variant="grid"` (default, admin Dashboard — unchanged)
 * keeps the original equal-weight 3-tile grid.
 */
export default function ContinueStudyingCard({ variant = "grid" }) {
  const [decks, setDecks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/dashboard/recent-decks`)
      .then((r) => setDecks(r.data || []))
      .catch(() => setDecks([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  if (variant === "hero") {
    const [primary, ...rest] = decks;
    // Never show a deck under "Recently Studied" that the user hasn't
    // actually studied — a fresh user's recent-decks response is ALL
    // is_suggestion tiles (no real history yet), and those belong only in
    // the hero's own "Start Studying" suggestion, never implied as history.
    const recentlyStudied = rest.filter((d) => !d.is_suggestion);
    return (
      <div className="space-y-4" data-testid="continue-learning-hero">
        <HeroCard deck={primary} />
        {recentlyStudied.length > 0 && (
          <div data-testid="recently-studied-strip">
            <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">
              Also Recently Studied
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {recentlyStudied.map((deck) => (
                <DeckTile key={deck.deck_name} deck={deck} compact />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const isSuggestion = decks.length > 0 && decks.every((d) => d.is_suggestion);
  const title = isSuggestion ? "Start Studying" : "Continue Studying";

  return (
    <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="continue-studying">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          {isSuggestion ? (
            <Sparkles size={18} className="text-[hsl(var(--primary))]" />
          ) : (
            <Layers size={18} className="text-[hsl(var(--primary))]" />
          )}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {decks.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {decks.map((deck) => (
              <DeckTile key={deck.deck_name} deck={deck} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <div
      className="rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40 p-6 text-center"
      data-testid="continue-studying-empty"
    >
      <Layers size={28} className="mx-auto text-[hsl(var(--muted-foreground))] mb-2" />
      <p className="text-sm font-medium">No decks yet</p>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
        Browse FlashSpecs to start learning cocktails, wines and more.
      </p>
      <Link
        to="/flashspecs"
        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--primary))] hover:gap-2 transition-all"
        data-testid="continue-studying-browse"
      >
        Open FlashSpecs <ArrowRight size={14} />
      </Link>
    </div>
  );
}

/** The ONE prominent resume action (Phase 6 hero). Falls back to a clear
 * route into FlashSpecs when there is nothing resumable yet — never a
 * fabricated recommendation. */
function HeroCard({ deck }) {
  if (!deck) {
    return (
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="continue-learning-empty">
        <CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))]/10 flex items-center justify-center shrink-0">
              <Layers size={22} className="text-[hsl(var(--primary))]" />
            </div>
            <div>
              <p className="font-bold">No study activity yet</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Open FlashSpecs to pick a deck and start learning.</p>
            </div>
          </div>
          <Link to="/flashspecs">
            <Button className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90" data-testid="continue-learning-cta">
              Open FlashSpecs <ArrowRight size={16} className="ml-1.5" />
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const isSuggestion = deck.is_suggestion;
  return (
    <Card className="bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--secondary))]/60 border-[hsl(var(--primary))]/30 overflow-hidden" data-testid="continue-learning-hero-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
          <div className="w-16 h-16 rounded-xl overflow-hidden bg-black/30 shrink-0 relative">
            {deck.cover_image_url ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${deck.cover_image_url}`}
                alt={deck.deck_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Layers size={24} className="text-[hsl(var(--muted-foreground))]" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--primary))] flex items-center gap-1">
              {isSuggestion ? <><Sparkles size={11} /> Start Studying</> : <><Clock size={11} /> Continue Studying</>}
            </p>
            <p className="font-bold text-lg truncate" data-testid="continue-learning-deck-name">{deck.deck_name}</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-2 sm:truncate" data-testid="continue-learning-detail">
              {isSuggestion
                ? `${deck.total_cards} card${deck.total_cards === 1 ? "" : "s"} to explore`
                : deck.last_card_name
                ? `Last studied ${deck.last_card_name} \u2022 ${deck.viewed_count} of ${deck.total_cards} cards \u2022 ${formatRelativeTime(deck.last_viewed_at)}`
                : `${deck.viewed_count} of ${deck.total_cards} cards studied`}
            </p>
            {!isSuggestion && (
              <div className="h-1.5 rounded-full bg-[hsl(var(--background))] mt-2 max-w-xs">
                <div className="h-full rounded-full bg-[hsl(var(--primary))]" style={{ width: `${deck.progress_pct || 0}%` }} />
              </div>
            )}
          </div>
          <Link to={deckHref(deck)} className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90 font-bold" data-testid="continue-learning-cta">
              <Play size={16} className="mr-1.5" />
              {isSuggestion ? "Start" : "Continue"}
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function DeckTile({ deck, compact = false }) {
  if (compact) {
    // Phase 6: a small horizontal row, deliberately lighter-weight than the
    // hero above it — a large thumbnail here would visually out-compete the
    // one prominent "Continue Studying" action.
    return (
      <Link
        to={deckHref(deck)}
        className="group flex items-center gap-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] hover:border-[hsl(var(--primary))]/60 transition-colors p-2.5"
        data-testid={`recent-deck-${deck.deck_name.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <div className="w-9 h-9 rounded-md overflow-hidden bg-black/30 shrink-0 flex items-center justify-center">
          {deck.cover_image_url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${deck.cover_image_url}`}
              alt={deck.deck_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <Layers size={16} className="text-[hsl(var(--muted-foreground))]" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-xs leading-tight truncate">{deck.deck_name}</p>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{deck.viewed_count}/{deck.total_cards} cards</p>
        </div>
        <ArrowRight size={13} className="text-[hsl(var(--primary))] shrink-0 group-hover:translate-x-0.5 transition-transform" />
      </Link>
    );
  }

  return (
    <Link
      to={deckHref(deck)}
      className="group rounded-xl overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] hover:border-[hsl(var(--primary))]/60 transition-colors flex flex-col"
      data-testid={`recent-deck-${deck.deck_name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="aspect-[16/9] bg-black/30 relative overflow-hidden">
        {deck.cover_image_url ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${deck.cover_image_url}`}
            alt={deck.deck_name}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Layers size={36} className="text-[hsl(var(--muted-foreground))]" />
          </div>
        )}
        {!deck.is_suggestion && (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-black/40">
            <div
              className="h-full bg-[hsl(var(--primary))]"
              style={{ width: `${deck.progress_pct || 0}%` }}
            />
          </div>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col gap-1">
        <p className="font-semibold text-sm leading-tight line-clamp-2">{deck.deck_name}</p>
        <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
          {deck.is_suggestion ? (
            <span className="flex items-center gap-1">
              <Sparkles size={11} /> Suggested
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <Clock size={11} /> {formatRelativeTime(deck.last_viewed_at)}
            </span>
          )}
          <span>
            {deck.is_suggestion ? `${deck.total_cards} cards` : `${deck.viewed_count}/${deck.total_cards}`}
          </span>
        </div>
        <span className="mt-1 text-xs text-[hsl(var(--primary))] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
          {deck.is_suggestion ? "Start" : "Continue"} <ArrowRight size={12} />
        </span>
      </div>
    </Link>
  );
}

